export type MediapipeDelegate = 'CPU' | 'GPU';

export interface MediapipeDelegateResolution {
	delegate: MediapipeDelegate;
	/** Motivo del fallback a CPU; `null` si se usa GPU por defecto. */
	reason: string | null;
	webGlRenderer: string | null;
	adrenoModel: number | null;
	maliModel: number | null;
}

/** Adreno con driver GL problemático en MediaPipe GPU (issues #5908, #5867). */
const KNOWN_DEFECTIVE_ADRENO_MODELS = new Set([735]);

/** A partir de esta generación Adreno el delegate GPU de MediaPipe falla en móvil. */
const DEFECTIVE_ADRENO_MIN_MODEL = 830;

/**
 * Samsung Internet + Adreno reciente: fallos silenciosos tras unos segundos (One UI 7 / tfjs #8562).
 * Umbral conservador: 740+ en Samsung Browser.
 */
const SAMSUNG_BROWSER_DEFECTIVE_ADRENO_MIN = 740;

/**
 * Mali mid-range con stall MediaPipe GPU en PRO (NIF-978 / Cloud Logs).
 * G52: mediapipe#3587. G57/G68: volumen alto de polAnalysisDeadWhilePreviewAlive.
 * Otras Mali (p. ej. G710) siguen en GPU hasta nueva evidencia.
 */
const KNOWN_DEFECTIVE_MALI_MODELS = new Set([52, 57, 68]);

/** PowerVR con fallos GPU históricos (mediapipe#1303) + stalls PRO NIF-978. */
const KNOWN_DEFECTIVE_POWERVR_PATTERN = /PowerVR\s+Rogue\s+GE8320/i;

const ADRENO_MODEL_PATTERN = /Adreno\s*(?:\(TM\))?\s*(\d+)/i;
const MALI_MODEL_PATTERN = /Mali-G(\d+)/i;

export function parseAdrenoModel(renderer: string): number | null {
	const match = ADRENO_MODEL_PATTERN.exec(renderer);
	if (!match) {
		return null;
	}
	const model = Number.parseInt(match[1], 10);
	return Number.isFinite(model) ? model : null;
}

export function parseMaliModel(renderer: string): number | null {
	const match = MALI_MODEL_PATTERN.exec(renderer);
	if (!match) {
		return null;
	}
	const model = Number.parseInt(match[1], 10);
	return Number.isFinite(model) ? model : null;
}

export function getWebGlRenderer(): string | null {
	if (typeof document === 'undefined') {
		return null;
	}

	try {
		const canvas = document.createElement('canvas');
		const gl =
			canvas.getContext('webgl2') ??
			canvas.getContext('webgl') ??
			canvas.getContext('experimental-webgl');
		if (!gl) {
			return null;
		}

		const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
		if (!debugInfo) {
			return null;
		}

		return (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
	} catch {
		return null;
	}
}

export function isDefectiveMediapipeGpu(
	renderer: string | null,
	userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): { defective: boolean; reason: string | null } {
	if (!renderer) {
		return { defective: false, reason: null };
	}

	const maliModel = parseMaliModel(renderer);
	if (maliModel !== null && KNOWN_DEFECTIVE_MALI_MODELS.has(maliModel)) {
		return { defective: true, reason: `mali_g${maliModel}` };
	}

	if (KNOWN_DEFECTIVE_POWERVR_PATTERN.test(renderer)) {
		return { defective: true, reason: 'powervr_ge8320' };
	}

	const adrenoModel = parseAdrenoModel(renderer);
	if (adrenoModel === null) {
		return { defective: false, reason: null };
	}

	if (KNOWN_DEFECTIVE_ADRENO_MODELS.has(adrenoModel)) {
		return { defective: true, reason: `adreno_${adrenoModel}` };
	}

	if (adrenoModel >= DEFECTIVE_ADRENO_MIN_MODEL) {
		return { defective: true, reason: `adreno_${adrenoModel}_gte_${DEFECTIVE_ADRENO_MIN_MODEL}` };
	}

	if (/SamsungBrowser/i.test(userAgent) && adrenoModel >= SAMSUNG_BROWSER_DEFECTIVE_ADRENO_MIN) {
		return {
			defective: true,
			reason: `samsung_browser_adreno_${adrenoModel}`,
		};
	}

	return { defective: false, reason: null };
}

/** Resuelve CPU/GPU para tasks MediaPipe según GPU Adreno / Mali / navegador. */
export function resolveMediapipeDelegate(
	userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
	webGlRenderer: string | null = getWebGlRenderer(),
): MediapipeDelegateResolution {
	const adrenoModel = webGlRenderer ? parseAdrenoModel(webGlRenderer) : null;
	const maliModel = webGlRenderer ? parseMaliModel(webGlRenderer) : null;
	const { defective, reason } = isDefectiveMediapipeGpu(webGlRenderer, userAgent);

	if (defective) {
		return {
			delegate: 'CPU',
			reason,
			webGlRenderer,
			adrenoModel,
			maliModel,
		};
	}

	return {
		delegate: 'GPU',
		reason: null,
		webGlRenderer,
		adrenoModel,
		maliModel,
	};
}

/** Contexto GPU para telemetría cuando el análisis MediaPipe muere con preview vivo. */
export interface MediapipeGpuStallLogContext {
	webGlRenderer: string | null;
	adrenoModel: number | null;
	mediapipeDelegate: MediapipeDelegate;
	knownDefectiveGpu: boolean;
	cpuFallbackReason: string | null;
	/** Stall con delegate GPU no incluida en la heurística de fallback a CPU. */
	stallOnUnlistedGpu: boolean;
}

export function buildMediapipeGpuStallLogContext(
	userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
	webGlRenderer: string | null = getWebGlRenderer(),
): MediapipeGpuStallLogContext {
	const resolution = resolveMediapipeDelegate(userAgent, webGlRenderer);
	const { defective } = isDefectiveMediapipeGpu(resolution.webGlRenderer, userAgent);

	return {
		webGlRenderer: resolution.webGlRenderer,
		adrenoModel: resolution.adrenoModel,
		mediapipeDelegate: resolution.delegate,
		knownDefectiveGpu: defective,
		cpuFallbackReason: resolution.reason,
		stallOnUnlistedGpu: resolution.delegate === 'GPU',
	};
}
